import { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Checkbox,
  CheckboxGroup,
  Chip,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableColumn,
  TableHeader,
  TableRow
} from '@nextui-org/react';
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { auth, db } from '../lib/firebase';
import { useAuth } from '../hooks/useAuth';
// El modelo de los cupones es el MISMO que usa la app: se importa su archivo
// tal cual (deja TCCouponModel en global), así los productos, la limpieza y
// la forma del espejo no se copian aquí.
import '../../../renderer/config/cupones-modelo.js';

const Coupons = globalThis.TCCouponModel;

// Cupones de los modales de suscripción. Cada cupón es un doc en la colección
// `coupons` (historial: crear, activar, desactivar, eliminar) y vale para los
// productos que se elijan (Performance, Premium, Founders Edition). Puede
// haber varios activos, pero solo uno por producto. Los activos se espejan en
// config/subscriptionCoupon, que es el doc que la app Electron escucha por
// onSnapshot para pintar el aviso y los precios rebajados en las pestañas de
// esos productos. Es SOLO informativo: el usuario teclea el código en el
// Checkout de Stripe, así que debe existir allí como Promotion Code activo y
// limitado a los mismos productos.

const MIRROR_DOC = () => doc(db, 'config', 'subscriptionCoupon');

const KINDS = [
  { key: 'percent', label: 'Porcentaje (%)' },
  { key: 'amount', label: 'Importe fijo (€/$)' }
];

const CURRENCIES = [
  { key: 'EUR', label: 'EUR (€)' },
  { key: 'USD', label: 'USD ($)' }
];

// Lo que sale marcado en un cupón nuevo: Founders ya no se vende salvo en
// reactivaciones, así que se elige a propósito.
const DEFAULT_PRODUCTS = ['performance', 'premium'];

function formatAmount(value, currency) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '';
  const s = Number.isInteger(n) ? String(n) : n.toFixed(2);
  return currency === 'USD' ? `$${s}` : `${s}€`;
}

function formatDiscount(c) {
  return c.kind === 'amount' ? formatAmount(c.value, c.currency) : `${c.value}%`;
}

// «Premium», «Performance y Premium», «Performance, Premium y Founders Edition».
function productList(ids) {
  const names = ids.map(Coupons.productName);
  if (names.length <= 1) return names[0] || '';
  return `${names.slice(0, -1).join(', ')} y ${names[names.length - 1]}`;
}

// «Performance y Premium ya tienen activo OCT; Founders Edition ya tiene activo VUELVE. Desactívalos antes»
// (`then` es lo que se propone después: «antes o quita ese producto»…).
function conflictText(list, then) {
  const byCode = new Map();
  list.forEach((x) => byCode.set(x.code, [...(byCode.get(x.code) || []), x.product]));
  const what = [...byCode].map(([code, ids]) => `${productList(ids)} ya ${ids.length > 1 ? 'tienen' : 'tiene'} activo ${code}`).join('; ');
  return `${what}. ${byCode.size > 1 ? 'Desactívalos' : 'Desactívalo'} ${then}`;
}

// Mismo texto que verá el usuario en la app (versión es); en la app el
// código va en negrita y el descuento como pill verde inline.
function previewMessage({ kind, value, currency, code }) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  const c = code?.trim().toUpperCase() || 'CUPÓN';
  const off = kind === 'amount'
    ? formatAmount(n, currency)
    : `${Number.isInteger(n) ? n : n.toFixed(2)}%`;
  return kind === 'amount'
    ? `Usa el código ${c} en Stripe para ${off} de descuento en tu plan`
    : `Usa el código ${c} en Stripe para un ${off} de descuento en tu plan`;
}

function formatTs(value) {
  if (!value) return '—';
  const d = typeof value.toDate === 'function' ? value.toDate() : new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-ES', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
}

// El espejo que lee la app, con los cupones que quedan activos tras el cambio
// (sin ninguno, el doc se borra: la app deja de enseñar cupones).
function writeMirror(batch, activeList) {
  const mirror = Coupons.toMirror(activeList);
  if (!mirror.coupons.length) {
    batch.delete(MIRROR_DOC());
    return;
  }
  batch.set(MIRROR_DOC(), {
    ...mirror,
    updatedAt: serverTimestamp(),
    updatedBy: auth.currentUser?.uid || null
  });
}

function ProductChips({ ids }) {
  return (
    <div className="flex flex-wrap gap-1">
      {ids.map((id) => (
        <Chip key={id} size="sm" variant="flat" color={id === 'founders' ? 'warning' : id === 'premium' ? 'secondary' : 'primary'}>
          {Coupons.productName(id)}
        </Chip>
      ))}
    </div>
  );
}

// Las tres casillas de producto; en los que ya tienen otro cupón activo, cuál.
function ProductPicker({ value, onChange, takenBy, isDisabled, label = 'Productos' }) {
  return (
    <CheckboxGroup
      label={label}
      value={value}
      onValueChange={onChange}
      isDisabled={isDisabled}
      size="sm"
    >
      {Coupons.PRODUCTS.map((p) => (
        <Checkbox key={p.id} value={p.id}>
          <span className="text-sm">{p.name}</span>
          {takenBy[p.id] && (
            <span className="ml-2 text-xs text-default-400">activo: {takenBy[p.id]}</span>
          )}
        </Checkbox>
      ))}
    </CheckboxGroup>
  );
}

export default function Cupones() {
  const { canWrite } = useAuth();
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  const [code, setCode] = useState('');
  const [kind, setKind] = useState('percent');
  const [value, setValue] = useState('');
  const [currency, setCurrency] = useState('EUR');
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const [activateNow, setActivateNow] = useState(true);

  // Cambiar los productos de un cupón ya creado.
  const [editing, setEditing] = useState(null);
  const [editProducts, setEditProducts] = useState([]);

  useEffect(() => {
    const q = query(collection(db, 'coupons'), orderBy('createdAt', 'desc'));
    const unsub = onSnapshot(
      q,
      (snap) => {
        setItems(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
        setLoaded(true);
        setError(null);
      },
      (e) => {
        setError(e.message);
        setLoaded(true);
      }
    );
    return unsub;
  }, []);

  const preview = useMemo(
    () => previewMessage({ kind, value, currency, code }),
    [kind, value, currency, code]
  );

  const active = useMemo(() => items.filter((c) => c.active === true), [items]);
  const activeExcept = (id) => active.filter((c) => c.id !== id);

  // producto → código del cupón activo que lo tiene (sin contar `exceptId`).
  function takenMap(exceptId) {
    const map = {};
    activeExcept(exceptId).forEach((c) => {
      Coupons.cleanProducts(c.products).forEach((p) => { if (!map[p]) map[p] = c.code; });
    });
    return map;
  }
  const taken = takenMap(null);

  const createConflicts = activateNow ? Coupons.conflicts(active, products) : [];
  // El aviso en rojo, cuando ya se está escribiendo un cupón (en reposo basta
  // con el «activo: X» junto a cada producto).
  const showCreateConflicts = createConflicts.length > 0 && code.trim() !== '';

  async function handleCreate() {
    const n = Number(value);
    const cleanCode = code.trim().toUpperCase();
    if (!cleanCode) {
      setToast({ type: 'error', msg: 'El código del cupón es obligatorio.' });
      return;
    }
    if (!Coupons.CODE_RE.test(cleanCode)) {
      setToast({ type: 'error', msg: 'Código inválido: solo letras, números, guion y guion bajo (máx. 30).' });
      return;
    }
    if (!Number.isFinite(n) || n <= 0) {
      setToast({ type: 'error', msg: 'El valor debe ser un número mayor que 0.' });
      return;
    }
    if (kind === 'percent' && n > 100) {
      setToast({ type: 'error', msg: 'El porcentaje no puede superar 100.' });
      return;
    }
    const chosen = Coupons.cleanProducts(products);
    if (!chosen.length) {
      setToast({ type: 'error', msg: 'Elige al menos un producto.' });
      return;
    }
    if (createConflicts.length) {
      setToast({ type: 'error', msg: conflictText(createConflicts, 'antes o crea este sin activarlo.') });
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      const data = {
        code: cleanCode,
        kind,
        value: Math.round(n * 100) / 100,
        products: chosen,
        active: activateNow,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.uid || null
      };
      if (kind === 'amount') data.currency = currency;
      if (activateNow) data.activatedAt = serverTimestamp();

      const newRef = doc(collection(db, 'coupons'));
      const batch = writeBatch(db);
      batch.set(newRef, data);
      if (activateNow) writeMirror(batch, [...active, data]);
      await batch.commit();

      setCode('');
      setValue('');
      setToast({
        type: 'ok',
        msg: activateNow
          ? `Cupón ${cleanCode} creado y activo en ${productList(chosen)} — la app lo muestra al instante.`
          : `Cupón ${cleanCode} guardado en el historial (inactivo).`
      });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleActivate(c) {
    const clash = Coupons.conflicts(activeExcept(c.id), c.products);
    if (clash.length) {
      setToast({ type: 'error', msg: `No se puede activar ${c.code}: ${conflictText(clash, `antes o cambia los productos de ${c.code}.`)}` });
      return;
    }
    setSaving(true);
    setToast(null);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'coupons', c.id), { active: true, activatedAt: serverTimestamp() });
      writeMirror(batch, [...activeExcept(c.id), c]);
      await batch.commit();
      setToast({ type: 'ok', msg: `Cupón ${c.code} activado en ${productList(Coupons.cleanProducts(c.products))}.` });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleDeactivate(c) {
    setSaving(true);
    setToast(null);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'coupons', c.id), { active: false });
      writeMirror(batch, activeExcept(c.id));
      await batch.commit();
      setToast({ type: 'ok', msg: `Cupón ${c.code} desactivado — la app deja de mostrarlo.` });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(c) {
    if (!confirm(`¿Eliminar el cupón ${c.code} del historial?${c.active ? '\n\nEstá activo: la app dejará de mostrarlo.' : ''}`)) return;
    setSaving(true);
    setToast(null);
    try {
      const batch = writeBatch(db);
      batch.delete(doc(db, 'coupons', c.id));
      if (c.active) writeMirror(batch, activeExcept(c.id));
      await batch.commit();
      setToast({ type: 'ok', msg: `Cupón ${c.code} eliminado.` });
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  function openEdit(c) {
    setEditing(c);
    setEditProducts(Coupons.cleanProducts(c.products));
  }

  const editConflicts = editing?.active ? Coupons.conflicts(activeExcept(editing.id), editProducts) : [];
  const editChosen = Coupons.cleanProducts(editProducts);

  async function handleSaveProducts() {
    if (!editing || !editChosen.length || editConflicts.length) return;
    setSaving(true);
    setToast(null);
    try {
      const batch = writeBatch(db);
      batch.update(doc(db, 'coupons', editing.id), { products: editChosen });
      if (editing.active) writeMirror(batch, [...activeExcept(editing.id), { ...editing, products: editChosen }]);
      await batch.commit();
      setToast({ type: 'ok', msg: `Cupón ${editing.code}: ahora vale para ${productList(editChosen)}.` });
      setEditing(null);
    } catch (e) {
      setToast({ type: 'error', msg: e.message });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <h1 className="text-2xl font-semibold">Cupones</h1>
        <p className="text-sm text-default-500">
          Aviso de cupón y precios rebajados de los modales de suscripción. Cada
          cupón vale para los productos que elijas y puede haber varios activos,
          pero solo uno por producto. Informativo: el código se teclea en el
          pago (Stripe). Allí tiene que existir como Promotion Code limitado a
          los mismos productos (Cupón → «Aplicar a productos específicos»); si
          no, el código valdrá también en los demás.
        </p>
      </div>

      {loaded && (
        <div className="flex flex-wrap gap-2">
          {Coupons.PRODUCTS.map((p) => {
            const c = Coupons.forTab(Coupons.fromMirror({ coupons: active }), p.id);
            return (
              <Chip key={p.id} color={c ? 'success' : 'default'} variant="flat">
                {p.name}: {c ? `${c.code} (${formatDiscount(c)})` : 'sin cupón'}
              </Chip>
            );
          })}
        </div>
      )}

      {toast && (
        <div
          className={`rounded-md px-3 py-2 text-sm ${
            toast.type === 'ok'
              ? 'bg-success-50 text-success-700'
              : 'bg-danger-50 text-danger-700'
          }`}
        >
          {toast.msg}
        </div>
      )}

      {error && (
        <div className="rounded-md bg-danger-50 px-3 py-2 text-sm text-danger-700">
          {error}
        </div>
      )}

      {!loaded ? (
        <Spinner className="mt-8 self-center" />
      ) : (
        <div className="grid gap-4 lg:grid-cols-[minmax(280px,360px)_1fr]">
          <Card className="h-fit">
            <CardHeader className="pb-0">
              <span className="text-base font-semibold">Nuevo cupón</span>
            </CardHeader>
            <CardBody className="gap-3">
              <Input
                label="Código del cupón"
                placeholder="GRACIAS"
                value={code}
                onValueChange={(v) => setCode(v.toUpperCase())}
                maxLength={30}
                isDisabled={!canWrite || saving}
              />
              <Select
                label="Tipo de descuento"
                selectedKeys={[kind]}
                onSelectionChange={(keys) => {
                  const k = Array.from(keys)[0];
                  if (k) setKind(k);
                }}
                disallowEmptySelection
                isDisabled={!canWrite || saving}
              >
                {KINDS.map((k) => (
                  <SelectItem key={k.key}>{k.label}</SelectItem>
                ))}
              </Select>
              <Input
                type="number"
                label={kind === 'amount' ? 'Importe' : 'Porcentaje'}
                placeholder={kind === 'amount' ? '1' : '20'}
                min={0}
                step="0.01"
                value={value}
                onValueChange={setValue}
                isDisabled={!canWrite || saving}
                endContent={
                  <span className="text-default-400 text-sm">
                    {kind === 'amount' ? (currency === 'USD' ? '$' : '€') : '%'}
                  </span>
                }
              />
              {kind === 'amount' && (
                <Select
                  label="Moneda"
                  selectedKeys={[currency]}
                  onSelectionChange={(keys) => {
                    const k = Array.from(keys)[0];
                    if (k) setCurrency(k);
                  }}
                  disallowEmptySelection
                  isDisabled={!canWrite || saving}
                >
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.key}>{c.label}</SelectItem>
                  ))}
                </Select>
              )}
              <ProductPicker
                value={products}
                onChange={setProducts}
                takenBy={activateNow ? taken : {}}
                isDisabled={!canWrite || saving}
              />
              <Switch
                isSelected={activateNow}
                onValueChange={setActivateNow}
                isDisabled={!canWrite || saving}
                size="sm"
              >
                Activar al crear
              </Switch>
              {showCreateConflicts && (
                <p className="text-xs text-danger-600">
                  {conflictText(createConflicts, 'antes, quita ese producto o crea este sin activarlo.')}
                </p>
              )}
              <div className="rounded-md bg-default-100 px-3 py-2 text-sm">
                <div className="text-xs uppercase tracking-wide text-default-400">
                  Vista previa (es)
                </div>
                {preview ? (
                  <>
                    <span className="font-semibold">🏷️ {preview}</span>
                    <div className="mt-1 text-xs text-default-500">
                      {products.length
                        ? `Sale en ${productList(Coupons.cleanProducts(products))}; el resto de productos, sin descuento.`
                        : 'Elige al menos un producto.'}
                    </div>
                  </>
                ) : (
                  <span className="text-default-400">
                    Completa código y valor para ver el mensaje.
                  </span>
                )}
              </div>
              <Button
                color="primary"
                onPress={handleCreate}
                isLoading={saving}
                isDisabled={!canWrite || !products.length || createConflicts.length > 0}
              >
                Crear cupón
              </Button>
              {!canWrite && (
                <p className="text-xs text-warning-600">
                  Tu rol es de solo lectura — solo un admin completo puede editar.
                </p>
              )}
            </CardBody>
          </Card>

          <Card>
            <CardHeader className="pb-0">
              <span className="text-base font-semibold">
                Historial ({items.length})
              </span>
            </CardHeader>
            <CardBody>
              <Table aria-label="Historial de cupones" removeWrapper>
                <TableHeader>
                  <TableColumn>CUPÓN Y PRODUCTOS</TableColumn>
                  <TableColumn>DESCUENTO</TableColumn>
                  <TableColumn>ESTADO</TableColumn>
                  <TableColumn>CREADO</TableColumn>
                  <TableColumn>ÚLTIMO USO</TableColumn>
                  <TableColumn align="end">ACCIONES</TableColumn>
                </TableHeader>
                <TableBody emptyContent="Aún no hay cupones — crea el primero.">
                  {items.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>
                        <div className="flex flex-col items-start gap-1">
                          <span className="font-mono font-semibold">{c.code}</span>
                          <ProductChips ids={Coupons.cleanProducts(c.products)} />
                        </div>
                      </TableCell>
                      <TableCell>{formatDiscount(c)}</TableCell>
                      <TableCell>
                        {c.active ? (
                          <Chip size="sm" color="success" variant="flat">Activo</Chip>
                        ) : (
                          <Chip size="sm" color="default" variant="flat">Inactivo</Chip>
                        )}
                      </TableCell>
                      <TableCell className="text-default-500">{formatTs(c.createdAt)}</TableCell>
                      <TableCell className="text-default-500">{formatTs(c.activatedAt)}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap justify-end gap-2">
                          {c.active ? (
                            <Button
                              size="sm"
                              variant="flat"
                              color="warning"
                              onPress={() => handleDeactivate(c)}
                              isDisabled={!canWrite || saving}
                            >
                              Desactivar
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              variant="flat"
                              color="success"
                              onPress={() => handleActivate(c)}
                              isDisabled={!canWrite || saving}
                            >
                              Activar
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="flat"
                            onPress={() => openEdit(c)}
                            isDisabled={!canWrite || saving}
                          >
                            Productos
                          </Button>
                          <Button
                            size="sm"
                            variant="flat"
                            color="danger"
                            onPress={() => handleDelete(c)}
                            isDisabled={!canWrite || saving}
                          >
                            Eliminar
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardBody>
          </Card>
        </div>
      )}

      <Modal isOpen={!!editing} onClose={() => setEditing(null)} size="md">
        <ModalContent>
          <ModalHeader>
            <div className="flex flex-col">
              <span>Productos de {editing?.code}</span>
              <span className="text-xs font-normal text-default-400">
                {editing?.active ? 'Activo: el cambio llega a la app al instante.' : 'Inactivo: se aplicará cuando lo actives.'}
              </span>
            </div>
          </ModalHeader>
          <ModalBody className="gap-3">
            <ProductPicker
              label="Vale para"
              value={editProducts}
              onChange={setEditProducts}
              takenBy={editing?.active ? takenMap(editing.id) : {}}
              isDisabled={saving}
            />
            {!editChosen.length && (
              <p className="text-xs text-danger-600">Elige al menos un producto.</p>
            )}
            {editConflicts.length > 0 && (
              <p className="text-xs text-danger-600">
                {conflictText(editConflicts, 'antes o quita ese producto.')}
              </p>
            )}
            <p className="text-xs text-default-500">
              Acuérdate de limitar también el cupón en Stripe a los mismos productos.
            </p>
          </ModalBody>
          <ModalFooter>
            <Button variant="flat" onPress={() => setEditing(null)}>
              Cancelar
            </Button>
            <Button
              color="primary"
              onPress={handleSaveProducts}
              isLoading={saving}
              isDisabled={!editChosen.length || editConflicts.length > 0}
            >
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </div>
  );
}
